import { memo, useCallback, useContext, useEffect, useMemo, useState, useRef, createContext } from 'react'
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import MUIDataTable from "mui-datatables";
import axios from 'axios'
import { Button, Modal, Badge } from 'react-bootstrap';
import { tableTheme } from "../config/config"
import TableViewCol from './TableViewCol';
import AddTerm from './AddTerm';
import AddTermRelation from './AddTermRelation';
import qs from "qs";
import { api } from "../services/config"
import {
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { compareObjects, getSort } from '../utils/utils';
import { useOutletContext } from 'react-router-dom';
import { ProjectContext } from '../screens/ProjectScreen';
import ConnectorSvg, {
  CustomSort,
  createColumns, createOptions,
} from '../utils/termTableUtils';
import { TbArrowsSort, TbCheck, TbPencil, TbPlus, TbSitemap, TbSitemapOff, TbTrashFilled, TbRefresh } from "react-icons/tb"
import { HiXMark } from "react-icons/hi2"
import { FaCheckSquare } from "react-icons/fa"
import { MdDisabledByDefault } from "react-icons/md"
import AddBulkSC from "./AddBulkSC";
import TableHead from "./TableHead"

export const TermListTableContext = createContext();
const theme = createTheme(tableTheme);

function TermListTable() {
  const [selectedTermId, setSelectedTermId] = useState()
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [rowsSelected, setRowsSelected] = useState([])
  const [isLoaded, setIsLoaded] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [thisPage, setThisPage] = useState(0)

  // Term filters
  const [postagRegex, setPostagRegex] = useState('')
  const [surfaceRegex, setSurfaceRegex] = useState('')
  const [lemmaRegex, setLemmaRegex] = useState('')
  const [languageFilter, setLanguageFilter] = useState([])
  const [producerFilter, setProducerFilter] = useState([])
  const [producerRegex, setProducerRegex] = useState('')
  const [validationFilter, setValidationFilter] = useState([])
  const [finalValidationFilter, setFinalValidationFilter] = useState([])
  const [words_lte, setWords_lte] = useState()
  const [words_gte, setWords_gte] = useState()
  const [occurences_gte, setOccurences_gte] = useState()
  const [occurences_lte, setOccurences_lte] = useState()
  const [n_doc_gte, setN_doc_gte] = useState()
  const [n_doc_lte, setN_doc_lte] = useState()
  const [headRegex, setHeadRegex] = useState('')
  const [expRegex, setExpRegex] = useState('')
  const [isVedette, setIsVedette] = useState(0)
  const [inSemantic, setiInSemantic] = useState(0)
  const [sortOrder, setSortOrder] = useState([])
  const [addTermIsOpen, setAddTermIsOpen] = useState(false)
  const [duplicateFormIsOpen, setDuplicateFormIsOpen] = useState(false);

  const {
    projectState, setProjectState,
    termList, setTermList,
    count, setCount,
    termData, setTermData,
    setTreeState,
    headers
  } = useContext(ProjectContext);

  const data = useMemo(() => termList?.map(term => ({
    referenceId: [term.id, term.reference],
    ...term.form.source,
    ...term.documents,
    ...term.form,
    ...term
  })), [termList])

  const [alertMsgShow, setAlertMsgShow] = useState(false);
  const [modelShow, setModelShow] = useState(false);
  const [modelData, setModelData] = useState({})
  const tableRef = useRef(null)

  const { profileState } = useOutletContext();
  const { projectRole: { projectId, role } } = profileState

  const columns = createColumns()

  useEffect(() => {
    let params = {
      postag_regex: postagRegex,
      surface_regex: surfaceRegex,
      lemma_regex: lemmaRegex,
      producer_regex: producerRegex,
      language_in: (languageFilter[0] === undefined || languageFilter[0].length === 0) ? undefined : languageFilter,
      producer_in: (producerFilter[0] === undefined || producerFilter[0].length === 0) ? undefined : producerFilter,
      head_regex: headRegex,
      exp_regex: expRegex,
      sort: getSort(sortOrder),
      words_lte,
      words_gte,
      occurences_gte,
      occurences_lte,
      n_doc_gte,
      n_doc_lte,
      is_vedette: (isVedette !== 0) ? isVedette : undefined,
      in_semantic: (inSemantic !== 0) ? inSemantic : undefined,
      user_vote: [undefined, ""].includes(validationFilter[0]) ? undefined : validationFilter,
      status_in: [undefined, "", "non-vu"].includes(finalValidationFilter[0]) ? undefined : finalValidationFilter,
      status_isnull: finalValidationFilter[0] === "non-vu" ? true : undefined,
      limit: rowsPerPage,
    };

    const currentFilters = { ...params };
    delete currentFilters.limit;

    const lastFilters = { ...projectState.metaTerms };
    delete lastFilters.refresh;
    delete lastFilters.limit;
    delete lastFilters.offset;
    delete lastFilters.sortOrder;

    const filtersChanged = !compareObjects(currentFilters, lastFilters);

    let offset = thisPage * rowsPerPage;
    if (filtersChanged) {
      offset = 0;
      if (thisPage !== 0) setThisPage(0);
    }
    params.offset = offset;

    async function fetchTerms(params) {
      setIsLoading(true)
      await axios.get(
        `${api.project}${projectId}/terms/`,
        {
          params: params,
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: "repeat" });
          },
        },
        { headers }
      )
        .then(res => {
          setTermList(res.data.terms);
          setProjectState(projectState => ({
            ...projectState,
            metaTerms: {
              ...params,
              refresh: projectState.refresh
            },
          }))
          setCount(res.data.count)
          setIsLoaded(false)
          setIsLoading(false)
        })
        .catch(
          function (error) {
            setIsLoaded(false)
            setIsLoading(false)
            return Promise.reject(error)
          }
        )
    }
    const params_ = { ...params, refresh: projectState.refresh }
    !compareObjects(params_, projectState.metaTerms) && fetchTerms(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectState.refresh, postagRegex, surfaceRegex, lemmaRegex, languageFilter, producerFilter, producerRegex, headRegex, expRegex, validationFilter, finalValidationFilter, words_lte, words_gte, occurences_gte, occurences_lte, n_doc_gte, n_doc_lte, inSemantic, isVedette])

  const handleDeleteTerm = useCallback(async (ids) => {
    await axios.delete(`${api.project}${projectId}/terms/`, {
      data: { ids },
      headers
    });

    let params = {
      postag_regex: postagRegex,
      surface_regex: surfaceRegex,
      lemma_regex: lemmaRegex,
      producer_regex: producerRegex,
      language_in: (languageFilter[0] === undefined || languageFilter[0].length === 0) ? undefined : languageFilter,
      producer_in: (producerFilter[0] === undefined || producerFilter[0].length === 0) ? undefined : producerFilter,
      head_regex: headRegex,
      exp_regex: expRegex,
      sort: getSort(sortOrder),
      words_lte,
      words_gte,
      occurences_gte,
      occurences_lte,
      n_doc_gte,
      n_doc_lte,
      is_vedette: (isVedette !== 0) ? isVedette : undefined,
      in_semantic: (inSemantic !== 0) ? inSemantic : undefined,
      limit: rowsPerPage,
      offset: rowsPerPage * thisPage
    };
    axios.get(
      `${api.project}${projectId}/terms/`,
      {
        params: params,
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
      },
      { headers }
    )
      .then(res => {
        if (ids.includes(termData.id)) setTermData({})
        setTermList(res.data.terms)
        setCount(res.data.count)
        setIsLoaded(false)
      })
      .catch(
        function (error) {
          setIsLoaded(false)
          return Promise.reject(error)
        }
      )
  }, [projectId, headers, postagRegex, surfaceRegex, lemmaRegex, producerRegex, languageFilter, producerFilter, headRegex, expRegex, sortOrder, words_lte, words_gte, occurences_gte, occurences_lte, n_doc_gte, n_doc_lte, isVedette, inSemantic, rowsPerPage, thisPage, termData.id, setTermData, setTermList, setCount])

  const handleClickDeleteTerm = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    const termList_ = selectedTerms.filter(term => term.vedette_class);
    setModelShow(true);
    setModelData({
      header: "Suppression des termes",
      body: <>
        <p>Vous avez demandé la suppression de <strong>{selectedTerms.length}</strong> termes</p>
        {termList_.length > 0 && <div className="mb-3">
          <p>
            Veuillez noter que certains des termes que vous avez sélectionnés ne peuvent pas être supprimés car ils sont des termes vedettes.
          </p>
          <p> Voici la liste de ces termes:</p>
          <ul className="list_terms">
            {termList_.map(term => <li>{term.reference}</li>)}
          </ul>
        </div>}
        <Button
          className='m-1 p-1'
          size="sm"
          onClick={() => {
            const ids = selectedTerms.filter(term => !term.vedette_class).map(term => term.id)
            if (ids.length > 0) handleDeleteTerm(ids)
            setModelShow(false);
            setModelData({});
            setRowsSelected([])
          }}
        >
          Confirmer
        </Button>
      </>
    })
  }

  const handleDeleteVedette = async (ids, updateTermList) => {
    // First, fetch the class data to get hypernyms and associated classes
    const { data: { classes } } = await axios.put(
      `${api.project}${projectId}/sc/`,
      { ids },
      { headers }
    );

    // Collect all hypernyms and associated class IDs
    const hypernyms = classes.flatMap(sc => sc.hypernyms.map(h => h.id));
    const associated = classes.flatMap(sc => sc.associated.map(a => a.id));

    await axios.delete(`${api.project}${projectId}/sc/`, { data: { ids }, headers })
      .then(() => {
        updateTermList();
        if (ids.includes(termData?.vedette_class)) setTermData(termData => ({ ...termData, vedette_class: null }))
        // Update tree state after deleting semantic classes
        // Include their hypernyms, associated classes, and root
        setTreeState({
          updatedSemantics: [0, ...hypernyms, ...associated],
          item: `deleteVedette-${Date.now()}`
        });
      })
      .catch(
        function (error) {
          return Promise.reject(error)
        }
      )
  }

  const handleClickDeleteVedette = async () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    const ids = selectedTerms.filter(term => term.vedette_class).map(term => term.vedette_class);
    const { data: { classes } } = await axios.put(`${api.project}${projectId}/sc/`, { ids }, { headers })
    const hypernymClasses = classes.filter(semantic => semantic.hyponyms.length > 0).map(semantic => semantic.id)
    const termList_ = termList.filter(term => hypernymClasses.includes(term.vedette_class))
    console.log(classes, hypernymClasses, termList_);
    setModelShow(true);
    setModelData({
      header: "Décomposition de classes",
      body: <>
        <p>Vous avez demandé la suppression de statut vedette de <strong>{selectedTerms.length}</strong> termes</p>
        {termList_.length > 0 && <div className="mb-3">
          <p>
            Il n’est pas possible de défaire une classe qui a des hyponymes. Supprimer le lien entre la classe et ses hyponymes puis défaire la classe.
          </p>
          <p> Voici la liste de ces termes:</p>
          <ul className="list_terms">
            {termList_.map(term => <li>{term.reference}</li>)}
          </ul>
        </div>}
        <Button
          className='m-1 p-1'
          size="sm"
          onClick={() => {
            const ids = classes.filter(semantic => !hypernymClasses.includes(semantic.id)).map(semantic => semantic.id)
            const updateTermList = () => {
              setTermList(termList => termList.map(term => {
                if (ids.includes(term.vedette_class)) term.vedette_class = null
                if (term.classes.length === 1) term.classes = []
                return term
              }))
            }
            if (ids.length > 0) handleDeleteVedette(ids, updateTermList)
            setModelShow(false);
            setModelData({});
            setRowsSelected([])
          }}
        >
          Confirmer
        </Button>
      </>
    })
  }

  const handleClickValidate = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    handleValidation(1, selectedTerms.map(term => term.id));
    setRowsSelected([]);
  }

  const handleClickInValidate = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    setModelShow(true);
    setModelData({
      header: "Invalidation des termes",
      body: <>
        <p>Vous avez demandé l'invalidation de <strong>{selectedTerms.length}</strong> termes</p>
        <Button
          className='m-1 p-1'
          size="sm"
          onClick={() => {
            handleValidation(-1, selectedTerms.map(term => term.id))
            setModelShow(false);
            setModelData({});
            setRowsSelected([])
          }}
        >
          Confirmer
        </Button>
      </>
    })
  }

  const handleClickFinalValidate = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    handleFinalValidation(true, selectedTerms.map(term => term.id));
    setRowsSelected([]);
  }

  const handleClickFinalInValidate = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    setModelShow(true);
    setModelData({
      header: "Invalidation finale des termes",
      body: <>
        <p>Vous avez demandé l'invalidation finale de <strong>{selectedTerms.length}</strong> termes</p>
        <Button
          className='m-1 p-1'
          size="sm"
          onClick={() => {
            handleFinalValidation(false, selectedTerms.map(term => term.id))
            setModelShow(false);
            setModelData({});
            setRowsSelected([])
          }}
        >
          Confirmer
        </Button>
      </>
    })
  }

  const handleValidation = useCallback(async (vote, ids) => {
    setIsLoading(true)
    const payload = {
      vote,
      ids
    };
    axios.put(`${api.project}${projectId}/terms/vote/`, payload, { headers })
      .then(() => {
        setTermList(termList => termList.map(term => {
          if (ids.includes(term.id)) term.vote = vote;
          return term;
        }))
        if (ids.includes(termData?.id)) setTermData(termData => ({ ...termData, vote }))

        setProjectState(projectState => ({ ...projectState, refresh: !projectState?.refresh }))
        setIsLoading(false)
      })
  }, [headers, projectId, setProjectState, setTermData, setTermList, termData?.id])

  const handleFinalValidation = useCallback(async (status, ids) => {
    setIsLoading(true)
    const term = {
      status,
      ids
    };
    axios.put(`${api.project}${projectId}/terms/validate/`, term, { headers })
      .then(() => {
        setTermList(termList => termList.map(term => {
          if (ids.includes(term.id)) term.status = status
          return term
        }))
        if (ids.includes(termData?.id)) setTermData(termData => ({ ...termData, status }))

        setProjectState(projectState => ({ ...projectState, refresh: !projectState?.refresh }))
        setIsLoading(false)
      })
  }, [headers, projectId, setProjectState, setTermData, setTermList, termData?.id])

  const fetchTerm = useCallback(async (id) => {
    const { data } = await axios.get(`${api.project}${projectId}/terms/${id}`)
    setSelectedTermId(data.id);
    setTermData(data)
  }, [projectId, setTermData])

  const handleClickAddRelation = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    setModelShow(true);
    setModelData({
      type: "add_class",
      header: "Ajouter des relations entre termes",
      body: <AddTermRelation
        type="bulk"
        selectedTerms={selectedTerms}
        onClose={() => {
          setModelShow(false);
          setModelData({});
          setRowsSelected([])
        }}
      />,
    })
  }

  const handleCreateSemantic = () => {
    const selectedTerms = rowsSelected.map(index => termList[index]);
    const termList_ = selectedTerms.filter(term => term.vedette_class)
    setModelShow(true);
    setModelData({
      type: "add_class",
      header: "Création/Édition de classes",
      body: <AddBulkSC
        {...{
          modelShow: true,
          setModelShow,
          modelDataType: "add_class",
          setModelData,
          setRowsSelected,
          projectId,
          termList_, selectedTerms,
        }}
      />,
    })
  }

  const handleEditTerm = () => {
    const selectedTerm = rowsSelected.map(index => termList[index])[0];
    setDuplicateFormIsOpen(true);
    setTermData(selectedTerm)
  }

  const changepage = useCallback(async (page = thisPage, newRowsPerPage, newSortOrder) => {
    selectedTermId && setSelectedTermId()
    let sort
    if (newSortOrder && Array.isArray(newSortOrder)) {
      sort = getSort(newSortOrder)
      setSortOrder(newSortOrder)
    }
    else if (newSortOrder && newSortOrder.name !== "none") {
      sort = getSort([newSortOrder])
      setSortOrder([newSortOrder])
    }
    else sort = getSort(sortOrder)

    setThisPage(page)
    setIsLoading(true)
    setIsLoaded(true)

    tableRef.current.tableRef.scrollIntoView({
      block: "start",
      behavior: "smooth"
    });

    async function fetchTerms(limit = 10, offset = 0) {
      let params = {
        postag_regex: postagRegex,
        surface_regex: surfaceRegex,
        lemma_regex: lemmaRegex,
        producer_regex: producerRegex,
        language_in: (languageFilter[0] === undefined || languageFilter[0].length === 0) ? undefined : languageFilter,
        producer_in: (producerFilter[0] === undefined || producerFilter[0].length === 0) ? undefined : producerFilter,
        head_regex: headRegex,
        exp_regex: expRegex,
        sort,
        words_lte,
        words_gte,
        occurences_gte,
        occurences_lte,
        n_doc_gte,
        n_doc_lte,
        is_vedette: (isVedette !== 0) ? isVedette : undefined,
        in_semantic: (inSemantic !== 0) ? inSemantic : undefined,
        limit,
        offset
      };
      await axios.get(
        `${api.project}${projectId}/terms/`,
        {
          params: params,
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: "repeat" });
          },
        },
        { headers }
      )
        .then(res => {
          setTermList(res.data.terms)
          setCount(res.data.count)
          setIsLoading(false)
          setIsLoaded(false)

          setProjectState(projectState => ({
            ...projectState,
            metaTerms: {
              ...projectState.metaTerms,
              limit: (newRowsPerPage === undefined) ? rowsPerPage : newRowsPerPage,
              offset,
              sort: sort,
            }
          }))
        })
        .catch(
          function (error) {
            setIsLoaded(false)
            setIsLoading(false)
            return Promise.reject(error)
          }
        )
    }
    const offset = (newRowsPerPage === undefined) ? page * rowsPerPage : thisPage * newRowsPerPage
    setTimeout(() => {
      fetchTerms((newRowsPerPage === undefined) ? rowsPerPage : newRowsPerPage, offset)
    }, 1);
  }, [headers, expRegex, headRegex, inSemantic, isVedette, languageFilter, lemmaRegex, n_doc_gte, n_doc_lte, occurences_gte, occurences_lte, projectId, postagRegex, producerFilter, producerRegex, rowsPerPage, setProjectState, setTermList, setCount, sortOrder, surfaceRegex, thisPage, words_gte, words_lte, selectedTermId])

  const onCellClick = useCallback((_, cellMeta) => {
    fetchTerm(termList[cellMeta.rowIndex].id)
  }, [fetchTerm, termList])

  const options = useMemo(
    () => createOptions(
      termList,
      selectedTermId,
      rowsPerPage,
      count,
      onCellClick,
      isLoaded,
      thisPage,
      changepage,
      setRowsPerPage,
      rowsSelected, setRowsSelected,
      sortOrder.length === 1 ? sortOrder[0] : { name: "none", direction: "none" },
    ),
    [changepage, rowsSelected, count, isLoaded, onCellClick, rowsPerPage, selectedTermId, termList, thisPage, sortOrder]
  );

  const handleReset = () => {
    setPostagRegex('')
    setSurfaceRegex('')
    setLemmaRegex('')
    setLanguageFilter([])
    setProducerFilter([])
    setProducerRegex('')
    setValidationFilter([])
    setFinalValidationFilter([])
    setWords_lte()
    setWords_gte()
    setOccurences_gte()
    setOccurences_lte()
    setN_doc_gte()
    setN_doc_lte()
    setHeadRegex('')
    setExpRegex('')
    setIsVedette(0)
    setiInSemantic(0)
    setSortOrder([])
    setThisPage(0)
    setProjectState(ps => ({ ...ps, refresh: !ps.refresh }))
  }

  const handleClickShowSort = () => {
    setModelShow(true);
    setModelData({
      type: "sort",
      header: "Trier la liste des termes",
      body: <CustomSort
        {...{
          setModelShow, setModelData,
          sortOrder, changepage,
        }}
      />,
    })
  }

  return (
    <div style={{ height: "100%" }}>
      <Modal
        show={alertMsgShow}
        onHide={() => setAlertMsgShow(false)}
        aria-labelledby="deleteTermVedette"
        dialogClassName="w-75"
      >
        <Modal.Header closeButton>
        </Modal.Header>
        <Modal.Body closeButton>
          Vous ne pouvez pas supprimer ce terme car il est vedette d'une classe.
        </Modal.Body>
      </Modal>
      <Modal
        show={modelShow}
        onHide={() => {
          setModelShow(false)
          setModelData({})
        }}
        aria-labelledby="CustomModel"
        dialogClassName="mw-50"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modelData?.header}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body closeButton>{modelData?.body}</Modal.Body>
      </Modal>
      <div>
        {(role > 0) && <>
          {rowsSelected.length === 0 && <Tooltip title="Créer un terme">
            <Button className='m-1 p-1'
              size="sm"
              onClick={() => setAddTermIsOpen(true)}>
              <TbPlus />
            </Button>
          </Tooltip>}

          {rowsSelected.length >= 1 && <>
            {role >= 2 && <>
              <Tooltip title="Validation finale des termes">
                <Button className='m-1 p-1'
                  size="sm"
                  onClick={handleClickFinalValidate}>
                  <FaCheckSquare />
                </Button>
              </Tooltip>
              <Tooltip title="Invalidation finale des termes">
                <Button className='m-1 p-1'
                  size="sm"
                  onClick={handleClickFinalInValidate}
                >
                  <MdDisabledByDefault />
                </Button>
              </Tooltip>
            </>}

            <Tooltip title="Valider les termes">
              <Button className='m-1 p-1'
                size="sm"
                onClick={handleClickValidate}>
                <TbCheck />
              </Button>
            </Tooltip>
            <Tooltip title="Invalider les termes">
              <Button className='m-1 p-1'
                size="sm"
                onClick={handleClickInValidate}
              >
                <HiXMark />
              </Button>
            </Tooltip>
            {rowsSelected.length === 1 && <Tooltip title="Éditer le terme">
              <Button className='m-1 p-1'
                size="sm"
                onClick={handleEditTerm}>
                <TbPencil />
              </Button>
            </Tooltip>}
            <Tooltip title="Ajouter termes liés">
              <Button className='m-1 p-1'
                size="sm"
                onClick={handleClickAddRelation}>
                <ConnectorSvg className="mr-1" />
              </Button>
            </Tooltip>
            <Tooltip title="Créer/Éditer des classes">
              <Button className='m-1 p-1'
                size="sm"
                onClick={handleCreateSemantic}>
                <TbSitemap />
              </Button>
            </Tooltip>
            <Tooltip title="Défaire les classes">
              <Button className='m-1 p-1'
                size="sm"
                onClick={handleClickDeleteVedette}>
                <TbSitemapOff />
              </Button>
            </Tooltip>
            <Tooltip title="Supprimer les termes">
              <Button className='m-1 ml-4 p-1'
                size="sm"
                onClick={handleClickDeleteTerm}>
                <TbTrashFilled />
              </Button>
            </Tooltip>
          </>}
        </>}
        {rowsSelected.length === 0 && <Tooltip title="Tri avancé">
          <Button className='m-1 p-1'
            size="sm"
            onClick={handleClickShowSort}
          >
            <TbArrowsSort />
            {sortOrder.length > 1 && <Badge className="count_badge px-2 ml-1">
              {sortOrder.length}
            </Badge>}
          </Button>
        </Tooltip>}
        {rowsSelected.length === 0 && <Tooltip title="Réinitialiser les filtres et tris">
          <Button className='m-1 p-1'
            size="sm"
            onClick={handleReset}
          >
            <TbRefresh />
          </Button>
        </Tooltip>}
      </div>
      {addTermIsOpen && <Modal
        show={true}
        onHide={() => {
          setAddTermIsOpen(false)
        }}
        aria-labelledby="modal-sizes-title-md-2"
        dialogClassName="w-90"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <AddTerm
            type="addTerm"
            onClose={() => setAddTermIsOpen(false)}
          />
        </Modal.Body>
      </Modal>}
      {duplicateFormIsOpen && <Modal
        show={true}
        onHide={() => {
          setDuplicateFormIsOpen(false)
        }}
        aria-labelledby="modal-sizes-title-md-2"
        dialogClassName="w-90"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <AddTerm
            type="duplicateForm"
            onClose={() => setDuplicateFormIsOpen(false)}
          />
        </Modal.Body>
      </Modal>}
      <TermListTableContext.Provider
        value={{
          postagRegex, setPostagRegex,
          surfaceRegex, setSurfaceRegex,
          lemmaRegex, setLemmaRegex,
          languageFilter, setLanguageFilter,
          producerFilter, setProducerFilter,
          producerRegex, setProducerRegex,
          validationFilter, setValidationFilter,
          finalValidationFilter, setFinalValidationFilter,
          words_lte, setWords_lte,
          words_gte, setWords_gte,
          occurences_gte, setOccurences_gte,
          occurences_lte, setOccurences_lte,
          n_doc_gte, setN_doc_gte,
          n_doc_lte, setN_doc_lte,
          headRegex, setHeadRegex,
          expRegex, setExpRegex,
          isVedette, setIsVedette,
          inSemantic, setiInSemantic,
        }}
      >
        <MuiThemeProvider theme={theme}>
          <MUIDataTable
            ref={tableRef}
            title={<RenderTitle {...{ isLoading }} />}
            data={data}
            columns={columns}
            options={options}
            components={{
              TableViewCol,
              TableHead,
            }}
          />
        </MuiThemeProvider >
      </TermListTableContext.Provider>
    </div >
  )
}

export default memo(TermListTable)

function RenderTitle({ isLoading }) {
  return (
    <div style={{ height: "42px" }}>
      {isLoading &&
        <CircularProgress
          size={24}
          style={{
            marginLeft: 15,
            position: 'relative',
            top: 10
          }}
        />
      }
    </div>);
}
